import React from 'react';
import BigNumber from 'bignumber.js';
import { Check, AlertTriangle, DollarSign, Percent } from 'lucide-react';
import { CONTRACTS } from '../futures/config/contracts.ts';
import PocketField from './PocketField';
import PocketInstrument from './PocketInstrument';
import { PkRow, PkPnl, PkSection, PkHero, PkTwoCol, PkMainField, PkChipRow, PkChip, PkFees, PkFee, DirArrow, PkCols, PkCol, PkExits } from './PocketRows';
import { fmt, money, fmtLvl } from './pocketFormat';

const tickSpec = (c) => c ? `$${c.pointValue}/pt · ${c.tickSize} tick · $${c.tickValue}/tick` : '';
const CONTRACT_OPTIONS = Object.values(CONTRACTS).map((c) => ({ value: c.symbol, label: c.symbol, hint: tickSpec(c) }));
const fmtDev = (d) => `${d > 0 ? '+' : ''}${Number(d || 0).toFixed(1)}%`;

// flat-ish contract sizing card — numbers only (no "Risk:" / "from target" words)
const VariantCard = ({ title, contracts, sym, risk, dev, warn, selected, onClick }) => (
  <button onClick={onClick}
    className={`flex-1 rounded-lg px-2.5 py-2 border text-left transition-all ${selected ? 'border-white/25 bg-white/[0.05]' : warn ? 'border-[#f2767f]/30 hover:bg-white/[0.02]' : 'border-white/10 hover:bg-white/[0.03]'}`}>
    <div className={`text-[8.5px] uppercase tracking-[0.08em] mb-0.5 flex items-center gap-1 ${warn ? 'text-[#f2767f]' : selected ? 'text-[#cdd6e1]' : 'text-[#5b6573]'}`}>
      {title}{warn ? <AlertTriangle size={9} /> : selected ? <Check size={9} /> : null}
    </div>
    <div className="pk-mono text-[14px] text-[#eef3f8]">{contracts} {sym}</div>
    <div className="pk-mono text-[9px] text-[#5b6573]">{fmt(risk, { money: true })} · <span className={warn ? 'text-[#f2767f]' : ''}>{dev}</span></div>
  </button>
);

// Futures market — cosmo layout. The direction arrow sits on the Contracts header.
const PocketFutures = ({ mode = 'compact', hook }) => {
  const { values, updateValue, setValues, metrics, contractSpec, changeContract } = hook;
  const full = mode === 'full';
  const setField = (field) => (bn) => updateValue(field, bn);

  const side = metrics.positionType === 'SHORT' ? 'short' : metrics.positionType === 'LONG' ? 'long' : null;
  const tickSize = contractSpec?.tickSize || 0.25;
  const tickDecimals = new BigNumber(tickSize).decimalPlaces() || 2;
  const toTicks = (pts) => (pts instanceof BigNumber ? Math.round(pts.toNumber() / tickSize) : '--');
  const ratio = (v) => fmt(v, { dp: 2 });

  const riskIcon = values.riskUnit === 'percent'
    ? <Percent size={12} className="text-[#6b7480]" />
    : <DollarSign size={12} className="text-[#6b7480]" />;

  const tolerance = Number(values.riskOvershootTolerance) || 0;
  const aggrExceeds = Number(metrics.ceilDeviation) > tolerance;
  const showCards = metrics.floorContracts > 0 || metrics.ceilContracts > 0;

  const util = metrics.marginUtilization instanceof BigNumber ? metrics.marginUtilization.toNumber() : NaN;
  const status = isNaN(util) ? '--' : util > 50 ? 'Over-leveraged' : util >= 25 ? 'High' : 'Safe';

  return (
    <div className="pk-body">
      <PocketInstrument value={values.contract} options={CONTRACT_OPTIONS} sub={tickSpec(contractSpec)}
        onSelect={(c) => changeContract(c)} />

      <PkTwoCol>
        <PkMainField label="Entry" copyValue={values.entryPrice}><PocketField value={values.entryPrice} onCommit={setField('entryPrice')} decimals={tickDecimals} className="text-[12px]" /></PkMainField>
        <PkMainField label="Stop" copyValue={values.stopLoss}><PocketField value={values.stopLoss} onCommit={setField('stopLoss')} decimals={tickDecimals} className="text-[12px]" /></PkMainField>
      </PkTwoCol>
      <PkChipRow>
        <PkChip icon={riskIcon} title="Risk"><PocketField value={values.risk} onCommit={setField('risk')} decimals={2} className="text-[12px]" /></PkChip>
        <PkChip label={values.multiTpEnabled ? "TPs" : "R:R"} title={values.multiTpEnabled ? "Multiple take-profits — set in Settings" : "Reward : Risk"}>{values.multiTpEnabled
          ? <span className="pk-mono text-[#eef3f8] text-[12px]">{values.tpCount}</span>
          : <PocketField value={values.desiredRR} onCommit={setField('desiredRR')} suffix="x" decimals={1} className="text-[12px]" />}</PkChip>
      </PkChipRow>
      <PkChipRow>
        <PkChip label="Fee" title="Round-turn fee per contract"><PocketField value={values.roundTurnFee} onCommit={setField('roundTurnFee')} prefix="$" decimals={2} className="text-[12px]" /></PkChip>
      </PkChipRow>

      <div className="pk-sep" />

      {/* Contracts — direction arrow lives here; notional to the right */}
      <div className="flex items-center gap-2 text-[#828892] text-[9px] uppercase tracking-[0.14em] font-medium mt-1.5 mb-1">
        <span className="flex items-center gap-1.5 shrink-0">Contracts <DirArrow side={side} /></span>
        <span className="flex-1" />
        <span className="pk-mono text-[#5b6573] text-[9.5px] tracking-normal normal-case shrink-0">Notional {fmt(metrics.notionalValue, { money: true })}</span>
      </div>

      {showCards ? (
        <div className="flex gap-2 mb-1">
          <VariantCard title="Conservative" contracts={metrics.floorContracts} sym={values.contract} risk={metrics.floorRisk}
            dev={fmtDev(metrics.floorDeviation)} warn={false} selected={metrics.selectedVariant === 'floor'}
            onClick={() => setValues({ risk: metrics.floorRisk })} />
          <VariantCard title="Aggressive" contracts={metrics.ceilContracts} sym={values.contract} risk={metrics.ceilRisk}
            dev={fmtDev(metrics.ceilDeviation)} warn={aggrExceeds} selected={metrics.selectedVariant === 'ceil'}
            onClick={() => setValues({ risk: metrics.ceilRisk })} />
        </div>
      ) : (
        <div className="mb-1">
          <div className="pk-mono text-[18px] text-[#5b6573]">0 {values.contract}</div>
          <div className="flex items-center gap-1.5 text-[#f2767f] text-[11px] mt-0.5"><AlertTriangle size={11} /> Insufficient risk</div>
          <button onClick={() => setValues({ risk: metrics.riskPerContract })} className="mt-1.5 w-full text-[11px] py-1 rounded-md bg-white/[0.05] text-[#cdd6e1] hover:bg-white/[0.08] transition-colors">Set risk {fmt(metrics.riskPerContract, { money: true })}</button>
        </div>
      )}

      <PkHero label="Margin req." value={money(metrics.marginRequired)} sub={`Util ${fmt(metrics.marginUtilization, { dp: 1 })}%`} copyLabel="Margin required" />
      <PkPnl profit={money(values.multiTpEnabled && metrics.multiExit ? metrics.multiExit.totalNetProfit : metrics.netProfit)} loss={money(metrics.netRisk)} />

      {values.multiTpEnabled && metrics.multiExit && <PkExits data={metrics.multiExit} mode={values.tpMode} pricePrefix="$" decimals={tickDecimals} />}

      <PkCols>
        <PkCol>
          <PkSection label="Trade" />
          {values.multiTpEnabled && metrics.multiExit ? <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.multiExit.blendedNetRR)}x`} sub="net" copyLabel="Blended net R:R" />
            <PkRow sm label="Avg exit" value={`$${fmtLvl(metrics.multiExit.avgExitPrice, tickDecimals)}`} copyLabel="Average exit" />
          </> : <>
            <PkRow sm subLeft label="R:R" value={ratio(metrics.grossRR)} sub={ratio(metrics.netRR)} copyLabel="Gross R:R" />
            <PkRow sm subLeft label="Req R:R" value={ratio(metrics.requiredGrossRR)} sub={full ? `${ratio(metrics.netRR)} net` : null} copyLabel="Required R:R" />
          </>}
        </PkCol>
        <PkCol>
          <PkSection label="Distances" />
          <PkRow sm label="Stop" value={`${toTicks(metrics.stopPoints)}t`} copyLabel="Stop in ticks" />
          <PkRow sm label="Target" value={`${toTicks(metrics.targetPoints)}t`} copyLabel="Target in ticks" />
          <PkRow sm label="B/E" value={`${toTicks(metrics.breakEvenPoints)}t`} copyLabel="Break-even in ticks" />
          {!values.multiTpEnabled && <>
            <PkRow sm label="Nom TP" value={`$${fmtLvl(metrics.takeProfit, tickDecimals)}`} copyLabel="Nominal TP" />
            <PkRow sm label="Opt TP" value={`$${fmtLvl(metrics.optimizedTakeProfit, tickDecimals)}`} copyLabel="Optimized TP" />
          </>}
        </PkCol>
      </PkCols>

      {full && <>
        <PkSection label="Fees" />
        <PkFees cols={2}>
          <PkFee label="Round-turn" value={fmt(metrics.totalFees, { money: true })} copyLabel="Total round turn" />
          <PkFee label="Margin" value={fmt(metrics.marginRequired, { money: true })} copyLabel="Margin required" />
          <PkFee label="Utilization" value={`${fmt(metrics.marginUtilization, { dp: 1 })}%`} copyLabel="Utilization" />
          <PkFee label="Status" value={status} copyLabel="Margin status" />
        </PkFees>
      </>}
    </div>
  );
};

export default PocketFutures;
